import { useEffect, useState } from "react";
import { groupBy } from "lodash";
import {
  getQuotesV2,
  QuoteV2,
  SymbolV2,
  useGetQuotesV2,
} from "@rentablo/api/apiv1";
import { useToken } from "@rentablo/api";

interface UseQuotesForSymbolsProps {
  symbols: SymbolV2[];
}

export function useQuotesForSymbols({
  symbols,
}: UseQuotesForSymbolsProps): SymbolWithQuote[] {
  const { data: token } = useToken();
  const [quotes, setQuotes] = useState<Record<string, QuoteV2>>({});
  const { data: allQuotes } = useGetQuotesV2(
    {
      securityId: symbols?.[0]?.id ? [symbols[0].id] : [],
      quoteProvider: symbols?.[0]?.quoteProvider,
      optimal: false,
    },
    {
      enabled: symbols.length === 1,
    }
  );

  useEffect(() => {
    if (symbols?.length <= 1 || !token) return;

    const groups = groupBy(symbols, "quoteProvider");
    Object.entries(groups).forEach(([quoteProvider, symbols]) => {
      getQuotesV2(token.access_token, {
        securityId: symbols?.map((s) => s.id),
        quoteProvider,
      }).then((data) => {
        setQuotes((prev) => ({
          ...prev,
          ...data?.quotes?.reduce(
            (acc, quote) => {
              if (quote.id.split(/__|@/)?.[0]) {
                acc[quote.id.split(/__|@/)?.[0]] = quote;
              }
              return acc;
            },
            {} as Record<string, QuoteV2>
          ),
        }));
      });
    });
  }, [symbols, token]);

  if (symbols.length === 1)
    return (
      allQuotes?.quotes?.map((quote) => ({
        ...symbols[0],
        quote: quote,
      })) ?? []
    );

  return symbols.map((symbol) => ({
    ...symbol,
    quote: quotes[symbol.id],
  }));
}
